import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import history from '../../../history';
import {Routes} from '../../../routes/routes';
import {ReduxState} from '../../../store/types';
import {actions} from '../../../store';
import Config from '../../../config.json';
import {Permission} from '../../../store/types/Permission';
import {useRawPermissions} from '../../../hooks/usePermissions';
import {useIndividualTypeHook} from '../../../hooks/agents/useIndividualTypeHook';
import {useExtensionDetailsForm} from '../../../hooks/useExtensionDetailsForm';
import usePageTitle from '../../../hooks/usePageTitle';
import {getTabNumber} from '../../../utils/getTabNumber';
import {generateTabs} from '../../../utils/generateTabs';
import {convertBool} from '../../../utils/parseBool';
import Loader from '../../../components/Loader/Loader';
import AlertDialog from '../../../components/AlertDialog/AlertDialog';
import {DialogButton} from '../../../components/AlertDialog/DialogContainer';
import DetailsWrapper from '../../../components/DetailsWraper/DetailsWrapper';
import PermissionProvider from '../../../components/PermissionProvider/PermissionProvider';
import ErrorNotification from '../../../components/TabErrors/ErrorNotification';
import NotFound from '../../NotFound/NotFound';
import ExtensionDetailsHeader from './ExtensionDetailsHeader';
import {DetailsProps, EditExtensionForm, ExtensionDetailsPayload, useStyles} from './utils';
import PlanTab from './PlanTab';
import DeviceTab from './DeviceTab';
import ExtensionTab from './ExtensionTab';
import CallSettingsTab from './CallSettingsTab';
import CallScreeningTab from '../../../components/Tabs/CallScreeningTab';
import CallForwardingTab from '../../../components/Tabs/CallForwardingTab';
import AiTab from '../../../components/Tabs/AiTab';
import CallHistoryForm from '../../../components/Forms/Extensions/CallHistory/CallHistoryForm';
import {MainProductType} from '../../../components/Extensions/MainProductSelectFiled/utils';
import {indexOf} from 'lodash';
import {EXTENSION_NOT_FOUND_BY_ID_API_ERROR} from '../../../store/sagas/extensions/extensions/saga';
import {TabInterface} from "../../../components/Tabs/Tabs.utils";
import {SparkleTabIcon} from "../../../components/SoarkleTabIcon";


const ExtensionDetails: React.VFC<DetailsProps> = (
    {
        id,
        actionButtonVisible,
    }) => {
    usePageTitle();
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {isSupervisor} = useIndividualTypeHook()

    const {
        i_account,
        extension_id,
        i_c_ext,
        i_customer,
        account_id,
        um_domain
    } = useExtensionDetailsForm();

    const saveClicked = useSelector(
        (state: ReduxState) => state.tabs?.saveForms || false,
    );

    const [tabIndex, setTabIndex] = useState(
        getTabNumber([0, 1, 2, 3, 4, 5, 6, 7]),
    );

    const [allTabsLoaded, setAllTabsLoaded] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saveEnabled, setSaveEnabled] = useState(false);
    const [removeModal, setRemoveModal] = useState<{
        isOpen: boolean;
        name?: string;
        id?: string;
        i_customer?: number;
        i_c_ext?: number;
    }>();

    const formData = useRef<ExtensionDetailsPayload>();
    const permissions = useRawPermissions();

    const formsSubmitFuncRef = useRef<
        { [key in string]: (() => void) | undefined }
    >({
        details: undefined,
        callSettings: undefined,
        ai:undefined,
        callScreening: undefined,
        callForwarding: undefined,
        plan: undefined,
        device: undefined,
    });

    const formsErrorsValidationFuncRef = useRef<
        { [key in string]: (() => boolean) | undefined }
    >({
        details: undefined,
        callSettings: undefined,
        ai:undefined,
        callScreening: undefined,
        callForwarding: undefined,
        plan: undefined,
        device: undefined,
    });

    const formsIsDirtyFuncRef = useRef<{ [key in string]: boolean }>({
        details: false,
        callSettings: false,
        ai:false,
        callScreening: false,
        callForwarding: false,
        plan: false,
        device: false,
    });

    const extensionDetails = useSelector(
        (state: ReduxState) => state.extensions,
    );

    const sessionToken = useSelector(
        (state: ReduxState) => state.auth.access_token,
    );

    const {
        isFormSending,
        isFormDataLoading,
        isExtensionDeleted,
    } = useSelector((state: ReduxState) => state.extensions);


    const setSubmitFunc = useCallback((
        funcName: keyof typeof formsSubmitFuncRef.current,
        func: () => void,
    ) => {
        formsSubmitFuncRef.current[funcName] = func;
    }, []);

    const setFormIsValidFunc = useCallback((
        funcName: keyof typeof formsErrorsValidationFuncRef.current,
        func: () => boolean,
    ) => {
        formsErrorsValidationFuncRef.current[funcName] = func;
    }, []);



    useEffect(() => {

        if (!isFormDataLoading) {
            dispatch(
                actions.getExtensionBasicDetailsData.request({
                    id,
                }),
            );
        }

        if (i_account && i_c_ext && i_customer) {
            dispatch(
                actions.getExtensionTabData.request({
                    i_account,
                    i_c_ext,
                    i_customer,
                }),
            );
            dispatch(
                actions.getCallSettingsTabData.request({
                    id: account_id || '',
                    domain: um_domain || '',
                    i_account,
                }),
            );
            dispatch(actions.getPlanTabData.request({i_account}));
            dispatch(actions.getDeviceTabData.request({i_account}));
            dispatch(actions.getCallForwardingTabData.request({i_account}));
        }

        if (i_account && i_customer) {
            dispatch(
                actions.getCallScreeningTabData.request({
                    i_account,
                    i_customer,
                }),
            );
        }

    }, [id, i_account]);

    useEffect(() => {
        let loadingInProgress = false;

        if (isFormDataLoading) {
            loadingInProgress = true;
        } else if (extensionDetails) {
            Object.keys(extensionDetails).forEach((atr: string) => {
                if (
                    // @ts-ignore
                    extensionDetails[atr] &&
                    // @ts-ignore
                    extensionDetails[atr]?.isLoading === true
                ) {
                    loadingInProgress = true;
                }
            });

            if (!loadingInProgress && !isFormDataLoading) {
                setAllTabsLoaded(true);
            }
        }
    }, [extensionDetails]);

    useEffect(() => {
        if (allTabsLoaded) {
formData.current = {
                init: {...formData.current?.updated},
                updated: {...formData.current?.updated},
            };
        }
    }, [allTabsLoaded]);

    useEffect(() => {
        (allTabsLoaded && isDirty) || actionButtonVisible
            ? setSaveEnabled(true)
            : setSaveEnabled(false);
    }, [allTabsLoaded, isDirty]);

    useEffect(() => {
        if (isExtensionDeleted) {
            setTimeout(() => {
                history.push(Routes.Extensions);
            }, 1000);
        }
    }, [isExtensionDeleted]);

    const clearDetails = () => {
        history.push(Routes.Extensions);
    };

    const handleDataFormUpdate = useCallback((form: Partial<EditExtensionForm>) => {
formData.current = {
            init: {...formData.current?.init},
            updated: {...formData.current?.updated, ...form},
        };
    }, [formData]);

    const handleDataInitFormUpdate = useCallback((form: Partial<EditExtensionForm>) => {
        formData.current = {
            init: {...formData.current?.init, ...form},
            updated: {...formData.current?.updated},
        };
    }, [formData]);

    const showRemoveExtensionModal = useCallback(() => {
        if (i_c_ext && extension_id && i_customer) {
            setRemoveModal({
                isOpen: true,
                id: extension_id,
                name: extension_id,
                i_customer: i_customer,
                i_c_ext: i_c_ext,
            });
        }
    }, [i_c_ext, extension_id, i_customer]);

    const handleDeleteExtension = useCallback(() => {
        if (i_c_ext && extension_id && i_customer) {
            dispatch(
                actions.deleteCustomerExtensionDetailsView.request({
                    i_customer: i_customer,
                    id: extension_id,
                    i_c_ext: i_c_ext,
                }),
            );
            setRemoveModal({...removeModal, isOpen: false});
            setIsDirty(false);
        }
    }, [i_c_ext, extension_id, i_customer]);

    const submitAllForms = useCallback(() => {
        Object.keys(formsSubmitFuncRef.current).forEach((key) => {
            formsSubmitFuncRef.current?.[key]?.();
        });
    }, [formsSubmitFuncRef]);

    const markTabAsHaveError = (tabName: string, tabIndex: number, setTabError: boolean) => {
        dispatch(actions.setErrorInTab({name: tabName, index: tabIndex, markAsInvalid: setTabError}))
    }

    const areAllFormsValid = useCallback(() => {
        let formsAreValid = true;
        Object.keys(formsErrorsValidationFuncRef.current).forEach((key) => {
            let setTabError;

            if (
                formsErrorsValidationFuncRef.current[key] !== undefined &&
                !formsErrorsValidationFuncRef.current[key]?.()
            ) {
                formsAreValid = false;
                setTabError = true;
            } else {
                setTabError = false
            }
            // @ts-ignore
            markTabAsHaveError(tabsNameResolver[key], tabNames.indexOf(tabsNameResolver[key]), setTabError);

        });


        return formsAreValid;
    }, [formsErrorsValidationFuncRef]);

    const onSubmitExtensionDetails = useCallback((blockRedirection?: boolean) => {
        submitAllForms();

        let formsAreValid = true;
        setTimeout(() => {
            formsAreValid = areAllFormsValid();
        }, 0);

        setTimeout(() => {
            if (formsAreValid) {

                setTimeout(() => {
                    if (formData.current) {
                        const {init, updated} = formData.current;
                        if (i_c_ext && i_account && account_id) {
                            dispatch(
                                actions.editExtension.request({
                                    accountId: i_account,
                                    extensionId: i_c_ext,
                                    // @ts-ignore
                                    initialValues: init,
                                    // @ts-ignore
                                    changedValues: updated,
                                    redirectTab: tabIndex,
                                    id: account_id,
                                    domain: um_domain || '',
                                    blockRedirection: blockRedirection,
                                }),
                            );
                        }
                    }
                    dispatch(actions.saveForms({visible: false}))
                }, 0);
            } else {
                dispatch(actions.saveForms({visible: true}))
            }
        }, 1000);
    }, [formData, i_c_ext, i_account, account_id, tabIndex]);

    const checkAllDirtyState = useCallback(() => {
        let isAnyFormDirty = false;

        Object.keys(formsIsDirtyFuncRef.current).forEach((key) => {
            if (formsIsDirtyFuncRef.current[key]) {
                isAnyFormDirty = true;
            }
        });

        isAnyFormDirty ? setIsDirty(true) : setIsDirty(false);
    }, [formsIsDirtyFuncRef]);

    const setIsDirtyFunc = (
        funcName: keyof typeof formsSubmitFuncRef.current,
        isDirty: boolean,
    ) => {
        formsIsDirtyFuncRef.current[funcName] = isDirty;
        checkAllDirtyState();
    };

    // Stabilize sessionToken prop to avoid unnecessary child renders
    const stableSessionToken = useMemo(() => sessionToken ?? null, [sessionToken]);

    // Memoize tab props to keep references stable
    const tabProps = useMemo(() => ({
        handleSubmitForm: handleDataFormUpdate,
        handleInitDataChange: handleDataInitFormUpdate,
        handleDirtyChange: setIsDirtyFunc,
        handleSetSubmitFunc: setSubmitFunc,
        handleSetIsValidFunc: setFormIsValidFunc,
    } as TabInterface), [
        handleDataFormUpdate,
        handleDataInitFormUpdate,
        setIsDirtyFunc,
        setSubmitFunc,
        setFormIsValidFunc,
    ]);


    const tabsNameResolver = {
        details: t('screens:extensions.extension'),
        callSettings: t('screens:extensions.callSettings'),
        callForwarding: t('screens:extensions.callForwarding'),
        plan: t('screens:extensions.plan'),
        ai:'AI agent',
        device: t('screens:devices.device'),
        callScreening: t('screens:extensions.callScreening'),
    }

    const {tabNames, tabs, perm, tabsIcons} = useMemo(() => generateTabs(
        [
            {
                title: t('screens:extensions.extension'),
                permission:
                Permission.CloudPBX.Extensions.ExtensionDetails.Extension
                    .value,
                tab: (
                    <ExtensionTab
                        {...tabProps}
                        key={'tab1'}
                        prefixEnabled={convertBool(Config.PREFIX_DID_ENABLED) || false}
                        isActive={tabIndex == 0}
                        tabName={t('screens:extensions.extension')}
                        callback={() => {
                            markTabAsHaveError(t('screens:extensions.extension'), indexOf(tabNames, t('screens:extensions.extension')), true);
                            dispatch(actions.saveForms({visible: true}));
                        }}
                    />
                ),
            },
            {
                title: t('screens:extensions.callSettings'),
                permission:
                Permission.CloudPBX.Extensions.ExtensionDetails.CallSettings
                    .value,
                tab: <CallSettingsTab
                    {...tabProps}
                    key={'tab2'}
                    isActive={tabIndex == 1}
                    tabName={t('screens:extensions.callSettings')}

                />,
            },
            {
                title: t('screens:extensions.aiAgent'),
                permission:
                Permission.CloudPBX.Extensions.ExtensionDetails.AiAgent.value,
                icon: <SparkleTabIcon />,
                tab: (
                    <AiTab
                        {...tabProps}
                        key={'tab-ai'}
                        sessionToken={stableSessionToken}
                        isActive={tabIndex == 6}
                        tabName={'AI agent'}
                        callback={() => {
                            markTabAsHaveError('AI agent', indexOf(tabNames, 'AI agent'), true);
                            dispatch(actions.saveForms({visible: true}));
                        }}
                    />
                ),
            },
            {
                title: t('screens:extensions.callScreening'),
                permission:
                Permission.CloudPBX.Extensions.ExtensionDetails
                    .CallScreening.value,
                tab: (
                    <CallScreeningTab
                        {...tabProps}
                        extensionNumber={extension_id}
                        key={'tab4'}
                        isActive={tabIndex == 3}
                        tabName={t('screens:extensions.callScreening')}
                    />
                ),
            },
            {
                title: t('screens:extensions.callForwarding'),
                permission:
                Permission.CloudPBX.Extensions.ExtensionDetails
                    .CallForwarding.value,
                tab: <CallForwardingTab
                    {...tabProps} key={'tab5'}
                    isActive={tabIndex == 4}
                    tabName={t('screens:extensions.callForwarding')}
                />,
            },
            {
                title: t('screens:extensions.plan'),
                permission:
                Permission.CloudPBX.Extensions.ExtensionDetails.Plan.value,
                tab: (
                    <PlanTab
                        {...tabProps}
                        productType={MainProductType.Extensions}
                        key={'tab6'}
                        isActive={tabIndex == 5}
                        tabName={t('screens:extensions.plan')}
                    />
                ),
            },
            {
                title: t('screens:devices.device'),
                permission:
                Permission.CloudPBX.Extensions.ExtensionDetails.Device
                    .value,
                tab: (
                    <DeviceTab
                        {...tabProps}
                        key={'tab7'}
                        assignPermission={
                            Permission.CloudPBX.Extensions.ExtensionDetails
                                .Device.AssignDevice.value
                        }
                        releasePermission={
                            Permission.CloudPBX.Extensions.ExtensionDetails
                                .Device.ReleaseDevice.value
                        }
                        changePermission={
                            Permission.CloudPBX.Extensions.ExtensionDetails
                                .Device.ChangeDevice.value
                        }
                        isActive={tabIndex == 6}
                        tabName={t('screens:devices.device')}

                    />
                ),
            },
            {
                title: t('screens:calls.callHistory'),
                permission:
                Permission.CloudPBX.Extensions.ExtensionDetails.CallHistory
                    .value,
                tab: (
                    <CallHistoryForm
                        accountId={i_account}
                        key={'tab8'}
                        playPermission={Permission.CloudPBX.Extensions.ExtensionDetails
                            .CallHistory.CallDetailRecord
                            .PlayCallRecording.value
                    }
                        deletePermission={
                            Permission.CloudPBX.Extensions.ExtensionDetails
                                .CallHistory.CallDetailRecord
                                .DeleteCallRecording.value
                        }
                        downloadPermission={
                            Permission.CloudPBX.Extensions.ExtensionDetails
                                .CallHistory.CallDetailRecord
                                .DownloadCallRecording.value
                        }
                        recordPermission={
                            Permission.CloudPBX.Extensions.ExtensionDetails
                                .CallHistory.CallDetailRecord.value
                        }
                    />
                ),
            }
        ].filter(el => !Config.VOICE_AI_ADAPTER_URL.length ? el.title !== 'AI agent' : true),
        permissions
    ), [i_account, permissions]);


    const onTabChange = (_: React.ChangeEvent<{}>, index: any) => {
        areAllFormsValid();
        dispatch(actions.saveForms({visible: false}))
        setTabIndex(index);
    }

    if (extensionDetails?.extensionTabReducerApiError?.faultcode === EXTENSION_NOT_FOUND_BY_ID_API_ERROR) {
        return (
            <NotFound/>
        );
    }

    if (isFormDataLoading) {
        return (
            <div className={classes.loader}>
                <Loader dataQa="extension-list-loader"/>
            </div>
        );
    }

    return (
        <>
            <DetailsWrapper
                showActionButtons
                top={
                    <PermissionProvider
                        permission={
                            Permission.CloudPBX.Extensions.ExtensionDetails
                                .value
                        }
                    >
                        <ExtensionDetailsHeader/>
                    </PermissionProvider>
                }
                tabs={tabs}
                saveEnable={saveEnabled}
                tabsIcons={tabsIcons}
                preventIsOpen={isDirty}
                preventSavePress={() => onSubmitExtensionDetails(true)}
                onTabChange={onTabChange}
                tabsNames={tabNames}
                defaultTabIndex={tabIndex}
                onSavePress={() => onSubmitExtensionDetails(false)}
                onDeletePress={showRemoveExtensionModal}
                deleteText={t('screens:extensions.deleteExtension')}
                onBackPress={clearDetails}
                deleteEnable={!isSupervisor}
                deletePermission={
                    Permission.CloudPBX.Extensions.ExtensionDetails
                        .DeleteExtension.value
                }
                perm={perm}
                actionsCustomClass={classes.actionsPanel}
            >
                {isFormSending && (
                    <Loader
                        dataQa="extension-form-data-sending-loader"
                        absolutePosition
                        dataTestId={'send-data-loader'}
                    />
                )}
                <AlertDialog
                    isOpen={!!removeModal?.isOpen}
                    dataQa={'delete-extension-modal'}
                    contentClass="medium-width-modal-delete"
                    hideHeader={true}
                    description={t(
                        'screens:extensions.deleteExtensionDescription',
                        {name: removeModal?.name},
                    )}
                    dialogActionsButtons={[
                        <DialogButton
                            key="cancel"
                            label={t('common:cancel')}
                            onClick={() =>
                                setRemoveModal({
                                    ...removeModal,
                                    isOpen: false,
                                })
                            }
                        />,
                        <DialogButton
                            key="delete"
                            label={t('common:delete')}
                            className={classes.primaryModalButton}
                            onClick={handleDeleteExtension}
                        />,
                    ]}
                />
                <ErrorNotification tabName={tabNames[tabIndex]} isSubmitting={saveClicked}/>
            </DetailsWrapper>
        </>
    );
};

export default ExtensionDetails;
